import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import type * as React from 'react';

interface TableTruncationResult {
  containerRef: React.RefObject<HTMLDivElement | null>;
  tableRef: React.RefObject<HTMLTableElement | null>;
  isTruncated: boolean;
}

export const useTableTruncation = (enabled: boolean): TableTruncationResult => {
  const containerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const lastResult = useRef(false);

  const measure = useCallback((): void => {
    const container = containerRef.current;
    const table = tableRef.current;
    let shouldTruncate = false;

    if (enabled && container && table && container.clientWidth > 0) {
      const cells = Array.from(table.querySelectorAll<HTMLElement>('[data-table-truncation]'));
      const previousClassNames = cells.map(cell => cell.className);
      const previousLayout = table.style.tableLayout;

      // Measure the expanded table every time, including when it is currently compact.
      // Restore all styles synchronously so the measurement is never painted.
      try {
        cells.forEach(cell => {
          cell.className = 'block';
        });
        table.style.tableLayout = 'auto';
        shouldTruncate = table.scrollWidth > container.clientWidth + 1;
      } finally {
        cells.forEach((cell, index) => {
          cell.className = previousClassNames[index];
        });
        table.style.tableLayout = previousLayout;
      }
    }

    if (lastResult.current !== shouldTruncate) {
      lastResult.current = shouldTruncate;
      setIsTruncated(shouldTruncate);
    }
  }, [enabled]);

  // Covers React updates to rows, column visibility, headers and translations.
  useLayoutEffect(() => {
    measure();
  });

  useLayoutEffect(() => {
    const container = containerRef.current;
    const table = tableRef.current;
    if (!enabled || !container || !table) return;

    let frame: number | undefined;
    let disposed = false;
    const scheduleMeasurement = (): void => {
      if (disposed || frame !== undefined) return;
      frame = requestAnimationFrame(() => {
        frame = undefined;
        measure();
      });
    };

    let lastWidth = container.clientWidth;
    const resizeObserver = new ResizeObserver(() => {
      const width = container.clientWidth;
      if (width === lastWidth) return;
      lastWidth = width;
      scheduleMeasurement();
    });
    resizeObserver.observe(container);

    // Also covers text updates inside cell components without a parent render.
    const mutationObserver = new MutationObserver(scheduleMeasurement);
    mutationObserver.observe(table, { subtree: true, childList: true, characterData: true });

    const fonts = document.fonts;
    void fonts?.ready.then(scheduleMeasurement);
    fonts?.addEventListener('loadingdone', scheduleMeasurement);

    return (): void => {
      disposed = true;
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      fonts?.removeEventListener('loadingdone', scheduleMeasurement);
      if (frame !== undefined) cancelAnimationFrame(frame);
    };
  }, [enabled, measure]);

  return { containerRef, tableRef, isTruncated };
};
